import React from 'react';
import { Select } from 'antd';

import CostcodesTreeSelector from '../../costcodes/CostcodesTreeSelector';

// Import Components
import OnTraccrNumberInput from '../../common/inputs/OnTraccrNumberInput';
import BorderlessButton from '../../common/buttons/BorderlessButton';

// Import Helpers:
import { getFormattedCurrency } from '../../forms/formHelpers';
import colors from '../../constants/Colors';
import {
  currencyFormatter as formatter,
  currencyParser as parser,
} from '../../helpers/inputParsers';

const getStyle = (invalidDistributionMap, id) => {
  if (invalidDistributionMap[id]) {
    return { border: '1px solid red', borderRadius: 5 };
  }

  return {};
};

/**
 * Gets the count of uploadable categories for a costcode
 */
const getUploadableCategoryCount = (costcodeId, costcodeMap, invoiceUploadCategoryIdSet) => {
  const cc = costcodeMap?.[costcodeId];
  if (!cc) return 0;

  const { categoryIds = [] } = cc;
  return categoryIds.filter((catId) => invoiceUploadCategoryIdSet?.has(catId)).length;
};

export default ({
  isDisplay,
  disabledPhaseCostcodeKeys,
  costcodeFilter,
  onCostcodesFetched,
  onCostcodeOptionChange,
  onAmountChange,
  onCostcodeDelete,
  projectOptions,
  onProjectOptionChange,
  isProjectDisabled,
  projectMap = {},
  invalidDistributionMap = {},
  getCategoryOptionsForCostcode,
  onCategoryChange,
  costcodeMap = {},
  invoiceUploadCategoryIdSet = new Set(),
}) => [
  {
    title: 'Project',
    dataIndex: 'projectId',
    key: 'projectId',
    align: 'center',
    width: 175,
    render: (projectId, record = {}) => (
      isDisplay || !record?.isNew
        ? projectMap[projectId]?.name ?? ''
        : (
          <Select
            options={projectOptions}
            value={projectId}
            onChange={(val) => onProjectOptionChange(record?.id, val)}
            disabled={isProjectDisabled}
            style={{ width: '95%', ...getStyle(invalidDistributionMap, record?.id) }}
            showSearch
            optionFilterProp="label"
          />
        )
    ),
  },
  {
    title: 'Costcode',
    dataIndex: 'phaseCostcodeKey',
    key: 'phaseCostcodeKey',
    align: 'center',
    width: 175,
    render: (key, record) => {
      const {
        isNew,
        id,
        phaseName,
        costcodeName,
        code,
        projectId,
      } = record || {};

      if (isDisplay || !isNew) {
        return `${phaseName || 'Unphased'} - ${code} ${costcodeName}`;
      }

      return (
        <CostcodesTreeSelector
          projectId={projectId}
          includeGlobal={false}
          filterCostcode={costcodeFilter}
          includeCategoryIds
          disabledKeys={disabledPhaseCostcodeKeys}
          onCostcodesFetched={onCostcodesFetched}
          value={key}
          onSelect={(val) => onCostcodeOptionChange(id, val)}
          style={{ width: '95%', ...getStyle(invalidDistributionMap, id) }}
        />
      );
    },
  },
  {
    title: 'Category',
    dataIndex: 'categoryId',
    key: 'categoryId',
    align: 'center',
    width: 140,
    render: (categoryId, record) => {
      const {
        isNew,
        id,
        costcodeId,
        categoryName,
      } = record || {};

      // If no costcode selected yet, show placeholder
      if (!costcodeId) {
        return isDisplay ? '-' : null;
      }

      // Display mode: show category name
      if (isDisplay || !isNew) {
        return categoryName || '-';
      }

      // Edit mode: show select dropdown (pre-filled for single category, selectable for multiple)
      const categoryOptions = getCategoryOptionsForCostcode?.(record) || [];
      const uploadableCategoryCount = getUploadableCategoryCount(
        costcodeId,
        costcodeMap,
        invoiceUploadCategoryIdSet,
      );
      const isSingleCategory = uploadableCategoryCount === 1;

      return (
        <Select
          options={categoryOptions}
          value={categoryId}
          onChange={(val) => onCategoryChange?.(id, val)}
          style={{ width: '95%', ...getStyle(invalidDistributionMap, id) }}
          placeholder="Select category"
          disabled={isSingleCategory}
        />
      );
    },
  },
  {
    title: 'Amount',
    dataIndex: 'amount',
    key: 'amount',
    align: 'center',
    width: 125,
    render: (amount, record) => {
      const { id } = record || {};
      return (
        isDisplay ? getFormattedCurrency(amount)
        : <OnTraccrNumberInput
            style={{ width: '95%', marginTop: 5 }}
            value={amount}
            onChange={(val) => onAmountChange(id, val)}
            formatter={formatter}
            parser={parser}
            precision={2}
            step={0.01}
          />
      );
    }
  },
  {
    key:'action',
    dataIndex: 'id',
    align:'center',
    width: 60,
    render:(id) => {
      return (
        isDisplay ? null
          : <BorderlessButton
              title=''
              icon='delete'
              color={colors.ONTRACCR_RED}
              onClick={() => onCostcodeDelete(id)}
              style={{
                paddingRight:8,
                paddingLeft:0,
              }}
            />
      );
    },
  },
];
